import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { Store } from "@ngrx/store";
import {
    UntypedFormBuilder,
    UntypedFormArray,
    Validators,
    UntypedFormGroup,
    AbstractControl,
    ValidatorFn,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { forkJoin } from "rxjs";
import { DefaultWebshopConfig } from "../../../models/defaultWebshopConfig.model";
import { RotateValidator } from "../../../helpers/validators/RotateValidator";
import { BackendService } from "../../../services/backend.service";
import {
    WebshopTransporterConfig,
    WebshopTransporterConfigData,
} from "../../../models/webshopTransporterConfig.model";
import { Message } from "../../../models/message";
import { addMessage } from "../../../store/actions/message.actions";
import { NO_CUSTOM_DOCUMENT_COUNTRY_CODES } from "../../../helpers/constants";
import { finishBusy, startBusy } from "src/app/store/actions/busy.actions";
import { StatusCodes } from "http-status-codes";
import { E_EXTERNAL_PARTIES } from "src/app/helpers/constants/transporters";

type TransporterConfigurableFields = {
    transporterCode: string;
    configurableFields: string[];
};

@Component({
    selector: "app-webshops-config",
    templateUrl: "./webshops-config.component.html",
    styleUrls: ["./webshops-config.component.scss"],
})
export class WebshopsConfigComponent implements OnInit {
    userId: number;
    disabled: false;
    openAccordion: number | null = null;
    viawebshopId: string;
    defaultConfig: DefaultWebshopConfig[];
    webshopConfig: WebshopTransporterConfig[] = [];
    availableTransporters: TransporterConfigurableFields[];
    transporterConfig: WebshopTransporterConfig[] = [];

    readonly accountIdValidators: { [key in E_EXTERNAL_PARTIES]?: ValidatorFn } = {
        [E_EXTERNAL_PARTIES.DHL]: Validators.pattern("^[0-9]{8}$"),
        [E_EXTERNAL_PARTIES.BPOST]: Validators.pattern("^[0-9]{6}$"),
    };

    transporterForm = this.fb.group({
        configs: this.fb.array([]),
    });

    schengenCountries = NO_CUSTOM_DOCUMENT_COUNTRY_CODES;
    PARCEL_TYPES = ["MAILBOX", "NORMAL", "HEAVY", "*"];

    get configs() {
        return this.transporterForm.get("configs") as UntypedFormArray;
    }

    @Output() onCancel: EventEmitter<void> = new EventEmitter();

    constructor(
        private fb: UntypedFormBuilder,
        private backend: BackendService,
        private route: ActivatedRoute,
        private store: Store,
        private router: Router,
    ) {}

    ngOnInit() {
        this.userId = +this.route.snapshot.params["id"];
        if (!this.userId) {
            console.error("User ID not found in route parameters!");

            this.store.dispatch(
                addMessage({
                    message: Message.createErrorMessage("Fout", "Gebruiker ID niet gevonden."),
                }),
            );
            return;
        }
        this.loadConfig();
    }

    getFormGroup(i: number): UntypedFormGroup {
        return this.configs.controls[i] as UntypedFormGroup;
    }

    open(i: number) {
        if (this.openAccordion === i) {
            this.openAccordion = null;
        } else if (this.getFormGroup(i)?.get("active")?.value) {
            this.openAccordion = i;
        }
    }

    cancel() {
        this.onCancel.emit();
        this.router.navigate(["../"], { relativeTo: this.route });
    }

    getControls(i: number): { [key: string]: AbstractControl } {
        return this.getFormGroup(i)?.controls ?? {};
    }

    isValid(i: number, field: string): boolean {
        return this.getControls(i)[field]?.valid ?? false;
    }

    addConfig(configFields: TransporterConfigurableFields) {
        const foundConfig = this.webshopConfig?.find(
            (w) => w.transporterCode === configFields.transporterCode,
        );

        const specificPatternValidator =
            this.accountIdValidators[configFields.transporterCode as unknown as E_EXTERNAL_PARTIES];

        const accountValidators = specificPatternValidator ? [specificPatternValidator] : null;
        const inboundAccountValidators = specificPatternValidator
            ? [specificPatternValidator]
            : null;

        const group = this.fb.group({
            transporterCode: [configFields.transporterCode],
            active: [!!foundConfig],
            accountID: [foundConfig?.data?.accountID || "", accountValidators],
            inboundAccountID: [foundConfig?.data?.inboundAccountID || "", inboundAccountValidators],
            rotate: [foundConfig?.data?.rotate ?? "", [RotateValidator]],
            sendMail: [foundConfig?.data?.sendMail ?? false],
            sameDay: [foundConfig?.data?.sameDay ?? false],
        });

        this.configs.push(group);
    }

    generateTransporterForm(transporters: TransporterConfigurableFields[] | undefined): void {
        this.configs.clear();
        if (transporters) {
            transporters.forEach((t) => this.addConfig(t));
        }
    }

    loadConfig() {
        this.store.dispatch(startBusy());
        forkJoin([
            this.backend.getDefaultConfig(),
            this.backend.getWebshopConfigById(this.userId),
        ]).subscribe({
            next: ([defaultConfig, webshopConfig]) => {
                this.defaultConfig = defaultConfig || [];
                this.webshopConfig = webshopConfig || [];

                const transporters: TransporterConfigurableFields[] = (
                    this.defaultConfig || []
                ).map((t) => ({
                    transporterCode: t.transporterCode,
                    configurableFields: t.configurableFields,
                }));
                this.availableTransporters = transporters;
                this.generateTransporterForm(transporters);
                this.store.dispatch(finishBusy());
            },
            error: (err) => {
                console.error("Error loading configuration:", err);
                this.store.dispatch(finishBusy());
                this.store.dispatch(
                    addMessage({
                        message: Message.createErrorMessage(
                            "Laadfout",
                            "Kon de configuraties niet laden.",
                        ),
                    }),
                );
            },
        });
    }

    onSubmit() {
        this.transporterForm.markAllAsTouched();

        if (this.transporterForm.valid) {
            this.store.dispatch(startBusy());
            let transporterConfigPayload: WebshopTransporterConfig[] = [];

            try {
                this.defaultConfig.forEach((defaultConf: DefaultWebshopConfig) => {
                    const formConfigs = this.transporterForm.get("configs")?.value;
                    if (!Array.isArray(formConfigs)) {
                        throw new Error(
                            "Interne formulier structuur fout. 'configs' array niet gevonden.",
                        );
                    }
                    formConfigs.forEach(
                        (formConfig: {
                            active: boolean;
                            transporterCode: string;
                            accountID: string | undefined | null;
                            inboundAccountID: string | undefined | null;
                            rotate: number | string | null | undefined;
                            sendMail: boolean | null | undefined;
                            sameDay: boolean | null | undefined;
                        }) => {
                            let rotateValue: number | null = null;
                            if (
                                formConfig.rotate !== null &&
                                formConfig.rotate !== undefined &&
                                String(formConfig.rotate).trim() !== ""
                            ) {
                                const parsedRotate = parseInt(String(formConfig.rotate), 10);
                                if (!isNaN(parsedRotate)) {
                                    rotateValue = parsedRotate;
                                } else {
                                    console.warn(
                                        `Invalid rotate value ignored for ${formConfig.transporterCode}: ${formConfig.rotate}`,
                                    );
                                }
                            }
                            const existingConfig = this.webshopConfig?.find(
                                (w) => formConfig.transporterCode === w.transporterCode,
                            );
                            const priorities = existingConfig?.data?.priorities ?? [];

                            if (
                                defaultConf.transporterCode === formConfig.transporterCode &&
                                formConfig.active === true
                            ) {
                                const newConfig = new WebshopTransporterConfig(
                                    formConfig.transporterCode,
                                    new WebshopTransporterConfigData(
                                        priorities,
                                        formConfig.accountID || undefined,
                                        formConfig.inboundAccountID || undefined,
                                        rotateValue,
                                        formConfig.sameDay ?? null,
                                        formConfig.sendMail ?? null,
                                    ),
                                );
                                transporterConfigPayload.push(newConfig);
                            }
                        },
                    );
                });

                if (transporterConfigPayload.length > 0) {
                    this.backend
                        .putTransporterConfig(transporterConfigPayload, this.userId)
                        .subscribe({
                            next: () => {
                                this.store.dispatch(finishBusy());
                                const message = Message.createSuccessMessage(
                                    "Succes",
                                    "Configuratie is succesvol opgeslagen.",
                                );
                                this.store.dispatch(addMessage({ message }));

                                this.loadConfig();
                            },
                            error: (err: any) => {
                                this.store.dispatch(finishBusy());
                                console.error("Error saving transporter config:", err);

                                let errorTitle = "Fout bij Opslaan";
                                let errorMessage =
                                    "Er is een onbekende serverfout opgetreden. Probeer het later opnieuw.";

                                if (err instanceof Error && !(err as any).status) {
                                    errorMessage = `Netwerk- of clientfout: ${err.message}`;
                                } else if (err.status) {
                                    errorTitle = `Fout (${err.status})`;
                                    if (
                                        err.status === StatusCodes.UNPROCESSABLE_ENTITY &&
                                        err.error?.errors
                                    ) {
                                        errorTitle = "Validatiefouten";
                                        try {
                                            const validationMessages = Object.entries(
                                                err.error.errors,
                                            )
                                                .map(
                                                    ([field, messages]) =>
                                                        `${field}: ${(messages as string[]).join(
                                                            ", ",
                                                        )}`,
                                                )
                                                .join("; ");
                                            errorMessage = `Controleer de volgende velden: ${validationMessages}`;
                                        } catch {
                                            errorMessage = "Server retourneerde validatiefouten.";
                                        }
                                    } else if (
                                        err.error?.message &&
                                        typeof err.error.message === "string"
                                    ) {
                                        errorMessage = err.error.message;
                                    } else if (err.status === StatusCodes.INTERNAL_SERVER_ERROR) {
                                        errorMessage = "Interne serverfout. Contacteer beheer.";
                                    } else if (err.status === StatusCodes.BAD_REQUEST) {
                                        errorMessage = "Ongeldig verzoek. Controleer de gegevens.";
                                    } else if (
                                        err.status === StatusCodes.UNAUTHORIZED ||
                                        err.status === StatusCodes.FORBIDDEN
                                    ) {
                                        errorMessage = "U heeft geen rechten voor deze actie.";
                                    } else if (err.status === 0) {
                                        errorMessage =
                                            "Kon de server niet bereiken. Controleer de netwerkverbinding.";
                                        errorTitle = "Netwerkfout";
                                    }
                                }

                                const message = Message.createErrorMessage(
                                    errorTitle,
                                    errorMessage,
                                );
                                this.store.dispatch(addMessage({ message }));
                            },
                        });
                } else {
                    this.store.dispatch(finishBusy());
                    console.warn(
                        "No active transporter configurations resulted from the form to save.",
                    );
                    const message = Message.createNotification(
                        "Geen Wijzigingen",
                        "Er zijn geen actieve transporteur configuraties geselecteerd/gewijzigd om op te slaan.",
                    );
                    this.store.dispatch(addMessage({ message }));
                }
            } catch (buildError: any) {
                this.store.dispatch(finishBusy());
                console.error("Error building transporter config payload:", buildError);
                const message = Message.createErrorMessage(
                    "Interne Fout",
                    `Kon de configuratie niet voorbereiden voor opslag: ${buildError.message}`,
                );
                this.store.dispatch(addMessage({ message }));
            }
        } else {
            const validationErrors = this.getFormValidationErrors(this.transporterForm);

            console.warn("Form is invalid. Invalid controls:", validationErrors);

            const message = Message.createErrorMessage(
                "Formulier Onvolledig",
                "Controleer de rood gemarkeerde velden, het formulier bevat fouten.",
            );
            this.store.dispatch(addMessage({ message }));

            this.focusFirstInvalidField();
        }
    }

    /** Helper to recursively find validation errors in form group/array */
    private getFormValidationErrors(
        form: AbstractControl,
    ): { controlName: string; errorKey: string; errorValue: any; path: string }[] {
        let errors: { controlName: string; errorKey: string; errorValue: any; path: string }[] = [];
        const findErrors = (control: AbstractControl | null, path: string = "") => {
            if (!control) return;

            if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
                Object.keys(control.controls).forEach((key) => {
                    findErrors(control.get(key), path ? `${path}.${key}` : key);
                });

                const groupErrors = control.errors;
                if (groupErrors) {
                    Object.keys(groupErrors).forEach((errorKey) => {
                        errors.push({
                            controlName: "(Groep/Array)",
                            errorKey: errorKey,
                            errorValue: groupErrors[errorKey],
                            path: path || "(root)",
                        });
                    });
                }
            } else if (control.invalid) {
                const controlErrors = control.errors;
                if (controlErrors) {
                    Object.keys(controlErrors).forEach((errorKey) => {
                        errors.push({
                            controlName: path.split(".").pop() || "?",
                            errorKey: errorKey,
                            errorValue: controlErrors[errorKey],
                            path: path,
                        });
                    });
                }
            }
        };
        findErrors(form);
        return errors;
    }

    /** Helper to focus the first invalid form control element */
    private focusFirstInvalidField() {
        const firstInvalidControl = document.querySelector("form .ng-invalid");
        if (firstInvalidControl instanceof HTMLElement) {
            firstInvalidControl.scrollIntoView({ behavior: "smooth", block: "center" });

            setTimeout(() => firstInvalidControl.focus(), 100);
        }
    }

    close = () => {
        this.router.navigateByUrl("/dashboard");
    };
}
